import { type FC, useEffect, useState } from "react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ASSET_CATEGORIES,
  DEBT_CATEGORIES,
  ACCOUNT_TYPES,
} from "@/shared/constants/estateCategories";

interface ChangeItemTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCategory: string;
  currentItemType?: string;
  onConfirm: (newCategory: string, newItemType: string, classification: "asset" | "debt") => void;
  isUpdating?: boolean;
}

const allCategories = [...ASSET_CATEGORIES, ...DEBT_CATEGORIES];
const ASSET_SET = new Set<string>(ASSET_CATEGORIES);

export const ChangeItemTypeDialog: FC<ChangeItemTypeDialogProps> = ({
  open,
  onOpenChange,
  currentCategory,
  currentItemType,
  onConfirm,
  isUpdating,
}) => {
  const [category, setCategory] = useState(currentCategory);
  const [itemType, setItemType] = useState(currentItemType || "");

  useEffect(() => {
    if (open) {
      setCategory(currentCategory);
      setItemType(currentItemType || "");
    }
  }, [open, currentCategory, currentItemType]);

  const itemTypes = category ? ACCOUNT_TYPES[category] || [] : [];
  const hasChanged = category !== currentCategory || itemType !== (currentItemType || "");
  const needsType = itemTypes.length > 0 && !itemType;

  const handleConfirm = () => {
    if (!category || isUpdating || needsType) return;
    const classification = ASSET_SET.has(category) ? "asset" : "debt";
    onConfirm(category, itemType, classification);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Change item type</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label className="text-sm">Category</Label>
            <Select
              value={category}
              onValueChange={(val) => {
                setCategory(val);
                setItemType("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {allCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {itemTypes.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label className="text-sm">Type</Label>
              <Select value={itemType} onValueChange={setItemType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {itemTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="brand"
              disabled={!hasChanged || isUpdating || needsType}
              onClick={handleConfirm}
            >
              {isUpdating ? "Updating..." : "Update"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
